import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Moonbeam Integrates with Covalent, Bringing Data Accessibility to the Platform\ndate: 2022-01-31T04:25:15-06:00\ncategories: [\"ecosystem\"]\ndescription: Moonbeam Integrates with Covalent\n\nsubbrand_image: https://www.covalenthq.com/static/images/blog/202202/Moonbeam-banner.png\ntype: legacy\n---\n\n**VANCOUVER, February 2, 2022** - Covalent, the leading provider of indexing and querying solutions for blockchains, is excited to announce the integration of Moonbeam, the Ethereum-compatible smart contract platform on Polkadot.\n\n\n\n## Quote from Ganesh Swami, Covalent's CEO\n\n\n \n \"\n We're thrilled to be adding support for Moonbeam to provide best-in-class data for their developers and to continue to be part of this growing ecosystem after indexing Moonriver. We have been bullish on this team since very early on, as they are a key piece of building the Covalent network.\n \n\n\nThis integration provides developers with the need for rich and granular Moonbeam blockchain data to build out multi-chain applications. Covalent’s unified API brings full transparency and visibility to assets across all blockchain networks. Developers can easily switch from one blockchain to another by changing the chain_id path parameter.\n\nThe blockchain data available includes:\n\n* Token balances per address\n* Historical transactions per address\n* Historical prices by address and ticker symbol\n* Decoded log events \n\nCovalent has a long relationship with Moombeam as they also indexed [Moonriver](https://moonbeam.network/networks/moonriver/), a parachain deployment on the Kusama network and the Covalent Network also lives on the Moonbeam network.\n\n## Quote from Derek Yoo, CEO of Purestake/Moonbeam Network\n\n\n \n \"\n Blockchains make data transparent, but by default that data is raw making it very difficult to analyze, interpret, and understand. Covalent is helping us create meaningful KPIs around Moonbeam platform's usage. We are using these KPIs to understand the growth of our ecosystem and to focus our energy into the highest impact areas. Covalent’s comprehensive data set means we can iterate on and refine these KPIs as we learn more, without having to rebuild from scratch.\n \n\n\nFor more information on how to query the Moonbeam network, visit the Moonbeam and Covalent documentation sites below.\n\n**Docs**\n\n* [Covalent API Reference](https://www.covalenthq.com/docs/api/#/overview)\n* [Covalent Docs](https://www.covalenthq.com/docs/networks/moonbeam/)\n* [Moonbeam Docs](https://docs.moonbeam.network/builders/integrations/indexers/covalent/)\n\n**About the Moonbeam Network**\n\nMoonbeam is an Ethereum-compatible smart contract platform on the Polkadot network that makes it easy to build natively interoperable applications. This Ethereum compatibility allows developers to deploy existing Solidity smart contracts and DApp frontends to Moonbeam with minimal changes. As a parachain on the Polkadot network, Moonbeam will benefit from the shared security of the Polkadot relay chain and integrations with other chains that are connected to Polkadot. Currently in active development by PureStake, Moonbeam is expected to reach MainNet by Q3 2021. Learn more: [https://moonbeam.network/](https://moonbeam.network/?utm_source=website&utm_medium=news&utm_campaign=announcement&utm_content=subscan)\n\n\n\n\n";
const filepath = "/blog/moonbeam-announcement.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
